import clsx from 'clsx'
import type { SearchTileDensity } from 'components/SearchKit/SearchTiles/searchTileDensity'
import { getSearchTileDensityClassName } from 'components/SearchKit/SearchTiles/searchTileDensity'
import { rem } from 'polished'
import type { PropsWithChildren, ReactNode } from 'react'
import styled from 'styled-components'
import { _searchTileContainerQueryUp } from './_SearchTileBase'
import { searchTileContentSpacingStyles } from './searchTileLayoutStyles'

const Container = styled.div`
  grid-area: content;
  display: flex;
  flex-direction: column;
  min-height: auto;
  ${searchTileContentSpacingStyles}

  &.density-compact {
    min-height: ${rem(208)};
  }

  &.density-cro-compact {
    min-height: 0;
  }
`

const croCompactContentColumn = `
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  align-items: stretch;
`

const ContentContainer = styled.div`
  flex-grow: 1;
  grid-area: content;
  display: grid;
  min-width: 0;
  gap: ${rem(16)};
  grid-template:
    "content-primary" auto
    "content-secondary" min-content / 1fr;

  & > * {
    min-width: 0;
  }

  &.density-compact {
    gap: ${rem(12)};
    grid-template:
      "content-header" auto
      "content-primary" auto
      "content-secondary" auto / 1fr;
    align-items: start;
  }

  /*
   * CRO mosaic rows: keep inclusions under the title (align-start). Extra height from the
   * mosaic hero must not stretch auto tracks (which visually centers short inclusion lists).
   * Price sticks to the bottom via margin-top: auto on the secondary column.
   */
  &.density-cro-compact {
    ${croCompactContentColumn}
  }

  ${_searchTileContainerQueryUp.tablet} {
    grid-template: "content-primary content-secondary" 1fr / minmax(0, 1fr) ${rem(200)};

    &.density-compact {
      grid-template:
        "content-header" auto
        "content-primary" 1fr
        "content-secondary" auto / 1fr;
      align-items: start;
    }

    &.density-cro-compact {
      ${croCompactContentColumn}
    }
  }

  ${_searchTileContainerQueryUp.desktopSmall} {
    grid-template:
      "content-primary" auto
      "content-secondary" min-content / 1fr;

    &.density-compact {
      grid-template:
        "content-header" auto
        "content-primary" auto
        "content-secondary" auto / 1fr;
      align-items: start;
    }

    &.density-cro-compact {
      ${croCompactContentColumn}
    }
  }

  ${_searchTileContainerQueryUp.desktop} {
    grid-template: "content-primary content-secondary" 1fr / minmax(0, 1fr) ${rem(200)};

    &.density-compact {
      grid-template:
        "content-header" auto
        "content-primary" 1fr
        "content-secondary" auto / 1fr;
    }

    &.density-cro-compact {
      ${croCompactContentColumn}
    }
  }
`

const Extension = styled.div`
  &:empty {
    display: none;
  }
`

interface Props extends PropsWithChildren {
  /**
   * Supporting content that always sits at the bottom of the content
   */
  bottomExtension?: ReactNode
  /**
   * Supporting content that always sits at the top of the content
   */
  topExtension?: ReactNode
  /**
   * @default default
   */
  density?: SearchTileDensity
}

function _SearchTileContent({ children, bottomExtension, topExtension, density = 'default' }: Props) {
  const densityClassName = getSearchTileDensityClassName(density)

  return <Container className={densityClassName}>
    {!!topExtension && <Extension>{topExtension}</Extension>}
    <ContentContainer className={clsx(_SearchTileContent.name, densityClassName)}>
      {children}
    </ContentContainer>
    {!!bottomExtension && <Extension>{bottomExtension}</Extension>}
  </Container>
}

export default _SearchTileContent
